/**
 * A DraftFastGalleryItem is a ...
 * You can ...
 * Read more about DraftFastGalleryItems
 * in this [article](<LINK_TO_KB_ARTICLE>).
 */
export interface DraftItem {
  /**
   * Item ID
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Represents the current state of the Item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Gallery ID that the item is part of
   * @format GUID
   * @immutable
   */
  draftGalleryId?: string | null;
  /** @maxLength 50 */
  title?: string | null;
  /** @maxLength 1500 */
  description?: string | null;
  /** Link to an internal or external page */
  link?: Link;
  /** media of the item */
  media?: MediaItem;
  /**
   * Sort order of Item in its Gallery, needed for Internal use
   * @internal
   * @readonly
   */
  sortOrder?: number | null;
  /**
   * Represents the time this Item was created
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Represents the time this Item was last updated
   * @readonly
   */
  updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

export interface Link {
  /**
   * The type of the link
   * @internal
   */
  type?: LinkTypeWithLiterals;
  /**
   * Display text of the link.
   * `referenced_entity_field` carries this field's translations from a source entity onto an entity
   * that references it - concretely, DraftItem -> the published Item, whose translations are synced
   * from DraftItem rather than owned locally. Without it the translated label is served on the draft
   * read (so the editor looks right) and silently falls back to the origin language on the published
   * read, which is what site visitors get. It must be declared on this leaf: the collector only
   * honours the annotation on STRING / StringValue / RichContent and ignores it on message fields.
   * The path is the field's location in the source entity; every embedder of this type - pro-gallery
   * and fast-gallery, draft and published - places it at `link`, so one path is correct for all.
   * @maxLength 1000
   */
  text?: string | null;
  /**
   * Target URL of the link.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Whether the link opens in a new tab or window. One of:
   * * `'_blank'`: The link opens in a new tab or window.
   * * `'_self'`: The link opens in the same tab or window.
   * if more options are needed, be sure to change maxLength accordingly
   * @internal
   * @maxLength 6
   */
  target?: string | null;
  /**
   * Information about the link. Reserved for internal use.
   * @internal
   */
  wixLinkData?: WixLink;
}

export enum LinkType {
  UNDEFINED = 'UNDEFINED',
  /** external link */
  EXTERNAL = 'EXTERNAL',
  /** for internal usage using wixLinkData */
  INTERNAL = 'INTERNAL',
}

/** @enumType */
export type LinkTypeWithLiterals =
  | LinkType
  | 'UNDEFINED'
  | 'EXTERNAL'
  | 'INTERNAL';

/** The link object generated by panels in the editor and used by applications in Wix */
export interface WixLink extends WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

/** @oneof */
export interface WixLinkLinkOneOf {
  /** External link type */
  external?: ExternalLink;
  /** Page link type */
  page?: PageLink;
  /** Anchor link type */
  anchor?: AnchorLink;
  /** Dynamic page link type */
  dynamicPage?: DynamicPageLink;
  /** Document link type */
  document?: DocumentLink;
  /** Email link type */
  email?: EmailLink;
  /** Phone link type */
  phone?: PhoneLink;
  /** Address link type */
  address?: AddressLink;
  /** WhatsApp link type */
  whatsApp?: WhatsAppLink;
  /** TPA link type */
  tpaPage?: TpaPageLink;
}

export interface ExternalLink {
  /**
   * The url of the page
   * @format WEB_URL
   */
  url?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /** @maxSize 20 */
  rel?: LinkRelWithLiterals[];
}

/**
 * The 'rel' attribute of the link. The rel attribute defines the relationship between a linked resource and the current document.
 * Further reading (also about different possible rel types): https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
 * Following are the accepted 'rel' types by Wix applications.
 */
export enum LinkRel {
  /** Indicates that the current document's original author or publisher does not endorse the referenced document. */
  nofollow = 'nofollow',
  /** Instructs the browser to navigate to the target resource without granting the new browsing context access to the document that opened it. */
  noopener = 'noopener',
  /** No Referer header will be included. Additionally, has the same effect as noopener. */
  noreferrer = 'noreferrer',
  /** Indicates a link that resulted from advertisements or paid placements. */
  sponsored = 'sponsored',
}

/** @enumType */
export type LinkRelWithLiterals =
  | LinkRel
  | 'nofollow'
  | 'noopener'
  | 'noreferrer'
  | 'sponsored';

export interface PageLink {
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Where this link should open, supports _self and _blank or any name the user chooses. _self means same page, _blank means new page.
   * @maxLength 1000
   */
  target?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface AnchorLink {
  /**
   * The name of the anchor
   * @maxLength 1000
   */
  anchorName?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string;
  /**
   * The page id we want from the site
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DynamicPageLink {
  /**
   * The router that handles this link
   * @maxLength 1000
   */
  routerId?: string;
  /**
   * The path data we'd like
   * @maxLength 1000
   */
  innerRoute?: string;
  /**
   * The data id (from the JSON page) of the anchor that should be used
   * @maxLength 1000
   */
  anchorDataId?: string | null;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface DocumentLink {
  /**
   * The id of the document
   * @maxLength 1000
   */
  docId?: string;
  /**
   * The name of the document for download purposes
   * @maxLength 1000
   */
  name?: string | null;
  /** If this document can be indexed by scrapers, default is false */
  indexable?: boolean;
}

export interface EmailLink {
  /**
   * The email we will be sending a message to
   * @format EMAIL
   */
  recipient?: string;
  /**
   * The subject of the email
   * @maxLength 1000
   */
  subject?: string | null;
  /**
   * The body of the email
   * @maxLength 100000
   */
  body?: string | null;
}

export interface PhoneLink {
  /**
   * The phone number we want to link to
   * @format PHONE
   * @maxLength 100
   */
  phoneNumber?: string;
}

export interface AddressLink {
  /**
   * An address that we can link to
   * @maxLength 1000
   */
  address?: string;
}

export interface WhatsAppLink {
  /**
   * The whatsApp phone number we want to connect with
   * @format PHONE
   */
  phoneNumber?: string;
}

/** Link to a TPA page */
export interface TpaPageLink {
  /**
   * Type of item (e.g. 'wix.stores.sub_pages.product')
   * @maxLength 1000
   */
  itemTypeIdentifier?: string;
  /**
   * Id of linked item
   * @maxLength 1000
   */
  itemId?: string;
  /**
   * Id of linked page
   * @maxLength 1000
   */
  pageId?: string;
  /**
   * Id of app being linked to (AppDefId)
   * @maxLength 1000
   */
  appDefinitionId?: string;
  /**
   * The relativepath of linked page
   * @maxLength 10000
   */
  path?: string;
  /**
   * rel of link
   * @maxSize 20
   */
  rel?: LinkRelWithLiterals[];
}

export interface MediaItem extends MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
  /** item's media type */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface MediaItemMediaOneOf {
  /** image item */
  imageOptions?: Image;
}

export enum Type {
  /** undefined type */
  UNDEFINED = 'UNDEFINED',
  /** image */
  IMAGE = 'IMAGE',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNDEFINED' | 'IMAGE';

export interface Image {
  /** Information about the Wix Media image. */
  imageInfo?: CommonImage;
  /** Focal point of the image. */
  focalPoint?: Point;
  /** Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif). */
  exif?: Record<string, any> | null;
  /**
   * Image compression level. <br />
   *
   * Min: `30` <br />
   * Max: `100`
   * @min 30
   * @max 100
   */
  quality?: number | null;
  /** [Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image. */
  unsharpMasking?: UnsharpMasking;
  /**
   * Whether the image is saved in secure media.
   * @internal
   */
  secure?: boolean | null;
  /**
   * Image alt text, translatable.
   * @maxLength 5000
   */
  altText?: string | null;
}

export interface CommonImage {
  /**
   * WixMedia image ID.
   * @maxLength 200
   */
  id?: string;
  /** Image URL. */
  url?: string;
  /** *Required.** Original image height. */
  height?: number;
  /** *Required.** Original image width. */
  width?: number;
  /**
   * Image alt text.
   * @maxLength 5000
   */
  altText?: string | null;
  /**
   * Image URL expiration date (when relevant).
   * @internal
   * @readonly
   */
  urlExpirationDate?: Date | null;
  /**
   * Image filename.
   * @readonly
   * @maxLength 512
   */
  filename?: string | null;
  /**
   * Image size in bytes.
   * @internal
   * @readonly
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:6 }
   */
  sizeInBytes?: string | null;
  /** Focal point of the image. */
  focalPoint?: FocalPoint;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface Point {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface UnsharpMasking {
  /**
   * Unsharp masking amount. Controls the sharpening strength. <br />
   *
   * Min: `0` <br />
   * Max: `5`
   * @max 5
   */
  amount?: number | null;
  /** Unsharp masking radius in pixels. Controls the sharpening width. */
  radius?: number | null;
  /**
   * Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />
   *
   * Min: `0` <br />
   * Max: `1`
   * @max 1
   */
  threshold?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ReorderedDraftItem {
  /**
   * The id of the gallery in which the reordering was done
   * @format GUID
   */
  galleryId?: string;
  /**
   * The id of the last DraftItem that was re-positioned
   * @format GUID
   */
  draftItemId?: string | null;
  /** New sortOrder of the last DraftItem that was re-positioned */
  sortOrder?: number | null;
}

export interface CreateDraftItemRequest {
  /** DraftFastGalleryItem to be created. */
  draftItem: DraftItem;
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

export interface CreateDraftItemResponse {
  /** The created DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface GetDraftItemRequest {
  /**
   * ID of the DraftFastGalleryItem to retrieve.
   * @format GUID
   */
  draftItemId: string;
}

export interface GetDraftItemResponse {
  /** The requested DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface UpdateDraftItemRequest {
  /** DraftFastGalleryItem to be updated, may be partial. */
  draftItem: DraftItem;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateDraftItemResponse {
  /** Updated DraftFastGalleryItem. */
  draftItem?: DraftItem;
}

export interface DeleteDraftItemRequest {
  /**
   * Id of the DraftFastGalleryItem to delete.
   * @format GUID
   */
  draftItemId: string;
}

export interface DeleteDraftItemResponse {}

export interface QueryDraftItemsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDraftItemsResponse {
  /** List of DraftFastGalleryItems. */
  draftItems?: DraftItem[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated Item. */
  draftItem?: DraftItem;
}

export interface BulkCreateDraftItemsRequest {
  /**
   * List of DraftFastGalleryItems to be created
   * @minSize 1
   * @maxSize 100
   */
  draftItems: DraftItem[];
  /** set to `true` if you wish to receive back the created DraftFastGalleryItems in the response */
  returnEntity?: boolean;
  /** If true, the item will be positioned first in the gallery. If not present or false, the items will be positioned last. */
  positionFirst?: boolean | null;
}

export interface BulkCreateDraftItemsResponse {
  /**
   * List of the bulk create operation results including the DraftFastGalleryItems and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDraftItemResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkDraftItemResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: DraftItem;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateDraftItemsRequest {
  /**
   * List of DraftFastGalleryItems to be updated.
   * @minSize 1
   * @maxSize 100
   */
  draftItems: MaskedDraftItem[];
  /** set to `true` if you wish to receive back the updated DraftFastGalleryItems in the response */
  returnEntity?: boolean;
}

export interface MaskedDraftItem {
  /** DraftFastGalleryItem to be updated, may be partial */
  draftItem?: DraftItem;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface BulkUpdateDraftItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateDraftItemsResponseBulkDraftItemResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateDraftItemsResponseBulkDraftItemResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: DraftItem;
}

export interface BulkDeleteDraftItemsRequest {
  /**
   * DraftFastGalleryItem ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  draftItemIds: string[];
}

export interface BulkDeleteDraftItemsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteDraftItemsResponseBulkDraftItemResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteDraftItemsResponseBulkDraftItemResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface RemoveDraftItemFromTrashBinRequest {
  /**
   * ID of the DraftItem to remove from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface RemoveDraftItemFromTrashBinResponse {}

export interface RestoreDraftItemFromTrashBinRequest {
  /**
   * ID of the DraftItem to restore from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface RestoreDraftItemFromTrashBinResponse {
  /** The restored DraftItem */
  draftItem?: DraftItem;
}

export interface GetDeletedDraftItemRequest {
  /**
   * ID of the DraftItem to retrieve from TrashBin
   * @format GUID
   */
  draftItemId: string;
}

export interface GetDeletedDraftItemResponse {
  /** The requested DraftItem */
  draftItem?: DraftItem;
}

export interface ListDeletedDraftItemsRequest {
  /** WQL expression */
  query?: CursorQuery;
  /**
   * List of DraftItem IDs to retrieve (supersedes the query)
   * @format GUID
   * @maxSize 100
   */
  draftItemIds?: string[];
}

export interface ListDeletedDraftItemsResponse {
  /** The retrieved deleted Galleries */
  draftItems?: DraftItem[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface MoveDraftItemsRequest {
  /**
   * DraftItem ids to be moved after `after_item_id`, if not provided, draftItems given will be positioned first
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  itemIds: string[];
  /**
   * Id of the draftItem which the items will be positioned after
   * @format GUID
   */
  afterItemId?: string | null;
}

export interface MoveDraftItemsResponse {}

export interface DraftItemNotFoundError {
  /**
   * The draft item id that was not found
   * @format GUID
   */
  draftItemId?: string;
}

export interface DraftItemNotInGalleryError {
  /**
   * List of gallery IDs where the draft items are located.
   * @format GUID
   * @maxSize 100000
   */
  galleryIds?: string[];
}

export interface SortDraftItemsByRequest {
  /** DraftItem ids to be sorted by field `sorting.field_name`, in direction `sorting.order` */
  sorting: SortByFieldSorting;
  /**
   * ID of the Gallery whose items are sorted
   * @format GUID
   */
  draftGalleryId: string;
}

export interface SortByFieldSorting {
  /** Name of the field to sort by. */
  fieldName?: SortableFieldNamesWithLiterals;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortableFieldNames {
  TITLE = 'TITLE',
}

/** @enumType */
export type SortableFieldNamesWithLiterals = SortableFieldNames | 'TITLE';

export interface SortDraftItemsByResponse {
  /**
   * Job ID for the sort operation (async-infra)
   * @format GUID
   */
  jobId?: string;
}

export interface DraftItemSortUnsupportedError {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type MoveDraftItemsApplicationErrors =
  | {
      code?: 'ITEM_NOT_FOUND';
      description?: string;
      data?: DraftItemNotFoundError;
    }
  | {
      code?: 'ITEMS_FROM_DIFFERENT_GALLERIES';
      description?: string;
      data?: DraftItemNotInGalleryError;
    };
/** @docsIgnore */
export type SortDraftItemsByApplicationErrors = {
  code?: 'INVALID_ARGUMENT';
  description?: string;
  data?: DraftItemSortUnsupportedError;
};
